import { useEffect, useRef, useState } from 'react';

type DealType = 'all' | 'just-arrived' | 'price-drops' | 'almost-sold' | 'last-chance';

interface DealFiltersProps {
  selectedFilter: DealType;
  onFilterChange: (filter: DealType) => void;
  counts: Record<DealType, number>;
}

const filters = [
  { id: 'all' as DealType, label: 'All Deals', icon: 'ri-radar-line', color: 'teal' },
  { id: 'just-arrived' as DealType, label: 'Just Arrived', icon: 'ri-truck-line', color: 'blue' },
  { id: 'price-drops' as DealType, label: 'Price Drops', icon: 'ri-arrow-down-circle-line', color: 'green' },
  { id: 'almost-sold' as DealType, label: 'Almost Sold', icon: 'ri-fire-line', color: 'orange' },
  { id: 'last-chance' as DealType, label: 'Last Chance', icon: 'ri-time-line', color: 'red' },
];

const activeStyles: Record<string, string> = {
  teal: 'border-teal-500 bg-teal-50',
  blue: 'border-blue-500 bg-blue-50',
  green: 'border-green-500 bg-green-50',
  orange: 'border-orange-500 bg-orange-50',
  red: 'border-red-500 bg-red-50',
};

const activeIconStyles: Record<string, string> = {
  teal: 'bg-teal-500 text-white',
  blue: 'bg-blue-500 text-white',
  green: 'bg-green-500 text-white',
  orange: 'bg-orange-500 text-white',
  red: 'bg-red-500 text-white',
};

const activeLabelStyles: Record<string, string> = {
  teal: 'text-teal-900',
  blue: 'text-blue-900',
  green: 'text-green-900',
  orange: 'text-orange-900',
  red: 'text-red-900',
};

const activeCountStyles: Record<string, string> = {
  teal: 'text-teal-700',
  blue: 'text-blue-700',
  green: 'text-green-700',
  orange: 'text-orange-700',
  red: 'text-red-700',
};

const activeCheckStyles: Record<string, string> = {
  teal: 'bg-teal-500',
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  orange: 'bg-orange-500',
  red: 'bg-red-500',
};

export default function DealFilters({ selectedFilter, onFilterChange, counts }: DealFiltersProps) {
  const [announcement, setAnnouncement] = useState('');
  const announcementRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const active = filters.find((f) => f.id === selectedFilter);
    if (!active) return;
    const count = counts[selectedFilter];
    setAnnouncement(
      `${active.label} filter selected — ${count} ${count === 1 ? 'deal' : 'deals'} shown`
    );
  }, [selectedFilter, counts]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      {/* Visually-hidden live region — announces filter changes to screen readers */}
      <p
        ref={announcementRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </p>

      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 flex items-center justify-center">
          <i className="ri-filter-3-line text-xl text-gray-700" aria-hidden="true"></i>
        </div>
        <h2 className="text-lg font-bold text-gray-900">Filter Deals</h2>
      </div>

      {/* Scrollable row on mobile, 5-col grid on md+ */}
      <div
        role="group"
        aria-label="Filter deals by type"
        className="flex gap-3 overflow-x-auto pb-1 md:pb-0 md:grid md:grid-cols-5 scrollbar-hide"
      >
        {filters.map((filter) => {
          const isActive = selectedFilter === filter.id;
          const count = counts[filter.id];

          return (
            <button
              key={filter.id}
              onClick={() => onFilterChange(filter.id)}
              aria-pressed={isActive}
              aria-label={`${filter.label}: ${count} ${count === 1 ? 'deal' : 'deals'}`}
              className={`relative flex-shrink-0 flex flex-col items-center gap-2 p-3 md:p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer w-28 md:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500 ${
                isActive
                  ? activeStyles[filter.color]
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full ${
                isActive ? activeIconStyles[filter.color] : 'bg-gray-100 text-gray-600'
              }`}>
                <i className={`${filter.icon} text-xl md:text-2xl`} aria-hidden="true"></i>
              </div>

              <div className="text-center">
                <div className={`text-xs md:text-sm font-bold whitespace-nowrap ${
                  isActive ? activeLabelStyles[filter.color] : 'text-gray-900'
                }`}>
                  {filter.label}
                </div>
                <div className={`text-xs ${
                  isActive ? activeCountStyles[filter.color] : 'text-gray-500'
                }`} aria-hidden="true">
                  {count} {count === 1 ? 'deal' : 'deals'}
                </div>
              </div>

              {isActive && (
                <div className={`absolute -top-1 -right-1 w-5 h-5 md:w-6 md:h-6 ${activeCheckStyles[filter.color]} rounded-full flex items-center justify-center`} aria-hidden="true">
                  <i className="ri-check-line text-white text-xs md:text-sm"></i>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
